import React from 'react';
import {
  BaseTheme,
  BaseThemeDark,
  BlueprintProvider,
  GlobalBaseTheme,
  globalDarkTheme,
  globalLightTheme,
  NewRelic,
  SCSSThemeProvider,
} from '@groma-dev/blueprint';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import { getBrowserLocale, parseLocationSearch } from '@groma-dev/scaffold';
import { AppConfig } from '../core/AppConfig';
import { AccreditationStore } from '../core/stores/AccreditationStore';
import DataStore from '../core/stores/DataStore';
import { InvestStore } from '../core/stores/InvestStore';
import OnboardingStore from '../core/stores/OnboardingStore';
import { defaultLocaleMessages } from '../core/locales';
import AppStore from './stores/AppStore';
import StoresProvider from './providers/StoresProvider';
import { ComponentFactory } from './ComponentFactory';

const initializeStores = () => {
  const accreditationStore = new AccreditationStore();
  const appStore = new AppStore();
  const dataStore = new DataStore();
  const investStore = new InvestStore();
  const onboardingStore = new OnboardingStore();

  return { accreditationStore, appStore, dataStore, investStore, onboardingStore };
};

const AppLoader = (): any => {
  const { accreditationStore, appStore, dataStore, investStore, onboardingStore } = initializeStores();
  const { Localization, Metrics } = AppConfig.Settings;
  const { defaultLocale, locales } = Localization;
  const { theme } = parseLocationSearch(window.location.search);
  const language = getBrowserLocale(navigator.language);
  const currentLocale: { [key: string]: string } = locales[language];
  const isLightTheme = theme === GlobalBaseTheme.LIGHT;

  if (Metrics.Firebase.enabled) {
    appStore.initializeFirebase(Metrics.Firebase.config);
  }

  if (Metrics.NewRelic.enabled) {
    const newRelic = new NewRelic();
    newRelic.initializeNewRelic(Metrics.NewRelic.config);
  }

  return (
    <BrowserRouter>
      <StoresProvider
        accreditationStore={accreditationStore}
        appStore={appStore}
        dataStore={dataStore}
        investStore={investStore}
        onboardingStore={onboardingStore}>
        <IntlProvider locale={language} key={language} messages={currentLocale} defaultLocale={defaultLocale}>
          <BlueprintProvider
            cssReset
            theme={isLightTheme ? globalLightTheme : globalDarkTheme}
            defineMessages={defaultLocaleMessages}>
            <SCSSThemeProvider theme={isLightTheme ? BaseTheme : BaseThemeDark}>
              <ComponentFactory.App />
            </SCSSThemeProvider>
          </BlueprintProvider>
        </IntlProvider>
      </StoresProvider>
    </BrowserRouter>
  );
};

export default AppLoader;
